// AS PENDÊNCIAS (18/10/2026): regras pequenas da tela, testadas à parte.

// A ordem dos grupos na gaveta: o que fala com o cliente primeiro.
export const ORDEM_DOS_GRUPOS = [
  'aviso_ao_cliente',
  'resposta_da_ia',
  'cobranca',
  // Fatia 4 (20/10/2026): o que a IA leu e ninguém decidia.
  'andamento_que_pede_acao',
  'honorario_a_lancar',
  'retomada',
  'tarefa_do_follow_up',
  // 19/10/2026: não é decisão obrigatória — a Andie cutuca sozinha; a equipe vê e assume se quiser.
  'lead_parado',
];

// Para onde cada item leva quando a decisão não cabe na gaveta.
export const destinoDo = (tipo, item, accountId) => {
  if (
    [
      'resposta_da_ia',
      'retomada',
      'tarefa_do_follow_up',
      'lead_parado',
    ].includes(tipo)
  ) {
    return item.conversation_id
      ? {
          name: 'inbox_conversation',
          params: { accountId, conversation_id: item.conversation_id },
        }
      : null;
  }
  if (tipo === 'cobranca') {
    return { name: 'financial_index', query: { aba: 'cobranca' } };
  }
  if (tipo === 'honorario_a_lancar') {
    // O honorário se lança no formulário de sempre, que a lista do topo do Financeiro abre já preenchido.
    return { name: 'financial_index' };
  }
  if (tipo === 'andamento_que_pede_acao' && item.processo_id) {
    return {
      name: 'legal_process_show',
      params: { accountId, processId: item.processo_id },
    };
  }
  return null;
};

// "Criar tarefa" a partir do andamento: a agenda abre o formulário já ligado ao processo, com o resumo da IA como
// descrição. Salva a tarefa, a leitura sai das Pendências (`leitura`). O resumo e o rótulo vão no `state` da
// navegação, não no endereço: texto de processo não fica no histórico do navegador.
export const tarefaDoAndamento = (item, accountId) => ({
  name: 'appointments_agenda',
  params: { accountId },
  query: {
    nova: '1',
    processo: String(item.processo_id),
    leitura: String(item.id),
  },
  state: { descricao: item.texto || '', rotulo: item.regra || '' },
});

// Quanto o item espera alguém, para o texto da tela: { n, unidade } com unidade MIN, H ou DIAS.
export const haQuanto = (iso, agora = Date.now()) => {
  if (!iso) return null;
  const minutos = Math.max(0, Math.round((agora - new Date(iso)) / 60000));
  if (minutos < 60) return { n: minutos, unidade: 'MIN' };
  const horas = Math.round(minutos / 60);
  if (horas < 24) return { n: horas, unidade: 'H' };
  return { n: Math.round(horas / 24), unidade: 'DIAS' };
};

// "4" — e "99+" para não estourar o botão.
export const numeroDoBotao = total => {
  if (!total) return '';
  return total > 99 ? '99+' : String(total);
};

export const gruposComItens = grupos =>
  ORDEM_DOS_GRUPOS.map(tipo => grupos.find(g => g.tipo === tipo)).filter(
    g => g && g.itens.length
  );

// ── O BOM DIA QUE ABRE SOZINHO, UMA VEZ POR DIA (21/10/2026) ─────────────────────────────────────────────────
// Victor: "aquelas pendências não estão despertando a curiosidade… quero que a pessoa seja chamada, uma vez ao dia".
// O "Bom dia" das Pendências já existia (abre pelo sininho das 7h30); aqui ele também abre sozinho no primeiro
// acesso do dia, quando há o que decidir. Preferências da pessoa (uiSettings, valem em qualquer computador):
//   aviso_pendencias_visto_em    'AAAA-MM-DD' (data LOCAL) do último dia em que abriu sozinho
//   aviso_pendencias_desligado   true = nunca mais sozinho (o botão e o sininho continuam)
export const CHAVE_AVISO_VISTO = 'aviso_pendencias_visto_em';
export const CHAVE_AVISO_DESLIGADO = 'aviso_pendencias_desligado';

// O lead parado não é decisão: a Andie cutuca sozinha e a equipe só vê (docs/produto/APROVACOES.md).
export const TIPOS_QUE_NAO_CHAMAM = ['lead_parado'];

// Novidade do produto (ModalDeNovidade): dois modais seguidos, nunca. ⚠️ Novidade nova = a `chave` entra aqui (o
// spec lê os componentes de escritorio/ e falha se alguma ficar de fora).
export const CHAVES_DAS_NOVIDADES = [
  'novidade_desfazer_envio_vista',
  'novidade_caixa_unica_vista',
  'novidade_copiloto_vista',
];

// A regra de abrir do ModalDeNovidade (escritorio/ModalDeNovidade.vue, `podeAbrir`), sem o intervalo entre novidades:
// ainda não vista e, se adiada ("Ver depois"), com menos de duas adiadas e a última há mais de um dia.
const UM_DIA = 24 * 60 * 60 * 1000;
const novidadePorAparecer = (valor, agora) => {
  if (!valor) return true;
  const r = typeof valor === 'object' ? valor : { vista_em: valor };
  if (r.vista_em) return false;
  const adiada = r.adiada_em ? new Date(r.adiada_em).getTime() : 0;
  return (r.vezes || 0) < 2 && agora - adiada > UM_DIA;
};
export const haNovidadePorAparecer = (ui = {}, agora = Date.now()) =>
  CHAVES_DAS_NOVIDADES.some(chave => novidadePorAparecer(ui[chave], agora));

// "AAAA-MM-DD" no fuso de quem está na tela: o "hoje" da pessoa, não o do servidor.
export const hojeLocal = (agora = new Date()) =>
  [
    agora.getFullYear(),
    String(agora.getMonth() + 1).padStart(2, '0'),
    String(agora.getDate()).padStart(2, '0'),
  ].join('-');

// Quantas pendências da pessoa pedem decisão (a contagem `por_tipo` do servidor, sem o lead parado).
export const quantasChamam = (porTipo = {}) =>
  Object.entries(porTipo).reduce(
    (soma, [tipo, n]) =>
      TIPOS_QUE_NAO_CHAMAM.includes(tipo) ? soma : soma + (Number(n) || 0),
    0
  );

// O Bom dia abre sozinho? `ui` = preferências da pessoa; vazias = ainda não carregaram (não decide).
export const bomDiaAbreSozinho = (ui, porTipo, agora = new Date()) => {
  if (!ui || !Object.keys(ui).length) return false;
  if (ui[CHAVE_AVISO_DESLIGADO]) return false;
  if (ui[CHAVE_AVISO_VISTO] === hojeLocal(agora)) return false;
  if (haNovidadePorAparecer(ui, agora.getTime())) return false;
  return quantasChamam(porTipo) > 0;
};

// Uma avaliação só por abertura do painel, mesmo com mais de um botão montado (barra larga e barra recolhida).
let bomDiaAvaliado = false;
export const bomDiaJaAvaliado = () => bomDiaAvaliado;
export const marcarBomDiaAvaliado = sim => {
  bomDiaAvaliado = sim;
};
